import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { StarRating } from './StarRating';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ReviewDialog = ({ open, onOpenChange }) => {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    rating: 5,
    comment: '',
    images: []
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/reviews`, {
        ...formData,
        lang: i18n.language
      });
      
      if (i18n.language === 'lv') {
        toast.success('Paldies par atsauksmi! Tā tiks pārbaudīta un drīzumā parādīsies vietnē.');
      } else {
        toast.success('Спасибо за отзыв! Он будет проверен и скоро появится на сайте.');
      }
      
      setFormData({
        name: '',
        email: '',
        rating: 5,
        comment: '',
        images: []
      });
      onOpenChange(false);
    } catch (error) {
      console.error('Error submitting review:', error);
      
      if (error.response?.status === 400) {
        if (i18n.language === 'lv') {
          toast.error('Jūs jau esat iesniedzis atsauksmi.');
        } else {
          toast.error('Вы уже оставили отзыв.');
        }
      } else {
        if (i18n.language === 'lv') {
          toast.error('Radās kļūda. Lūdzu, mēģiniet vēlreiz.');
        } else {
          toast.error('Произошла ошибка. Пожалуйста, попробуйте позже.');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="review-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl tracking-tight">
            {i18n.language === 'lv' ? 'Atstāt atsauksmi' : 'Оставить отзыв'}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          <div className="space-y-2">
            <Label htmlFor="name" className="font-body text-sm uppercase tracking-wide">
              {i18n.language === 'lv' ? 'Vārds' : 'Имя'} *
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              data-testid="review-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="font-body text-sm uppercase tracking-wide">
              E-pasts / Email *
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              data-testid="review-email-input"
            />
            <p className="text-xs text-muted-foreground">
              {i18n.language === 'lv' 
                ? 'Jūsu e-pasts netiks publicēts'
                : 'Ваш email не будет опубликован'}
            </p>
          </div>

          <div className="space-y-2">
            <Label className="font-body text-sm uppercase tracking-wide">
              {i18n.language === 'lv' ? 'Vērtējums' : 'Оценка'} *
            </Label>
            <StarRating 
              rating={formData.rating} 
              interactive={true}
              onChange={(rating) => setFormData({ ...formData, rating })}
              size={32}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="comment" className="font-body text-sm uppercase tracking-wide">
              {i18n.language === 'lv' ? 'Komentārs' : 'Комментарий'} *
            </Label>
            <Textarea
              id="comment"
              value={formData.comment}
              onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
              required
              rows={4}
              data-testid="review-comment-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="images" className="font-body text-sm uppercase tracking-wide">
              {i18n.language === 'lv' ? 'Foto (neobligāti)' : 'Фото (необязательно)'}
            </Label>
            <Input
              id="images"
              type="url"
              placeholder={i18n.language === 'lv' ? 'https://...' : 'https://...'}
              value={formData.images[0] || ''}
              onChange={(e) => setFormData({ ...formData, images: e.target.value ? [e.target.value] : [] })}
              data-testid="review-image-input"
            />
            <p className="text-xs text-muted-foreground">
              {i18n.language === 'lv' 
                ? 'Pievienojiet attēla saiti (URL)' 
                : 'Добавьте ссылку на изображение (URL)'}
            </p>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground uppercase tracking-widest transition-colors duration-500"
            data-testid="review-submit-button"
          >
            {loading 
              ? (i18n.language === 'lv' ? 'Sūta...' : 'Отправка...') 
              : (i18n.language === 'lv' ? 'Nosūtīt' : 'Отправить')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
